<template>
    <div class="annotation-form">
        <template v-if="isLoading || isSavingComment">
            <CompassSkeleton variant="square" />
            <CompassSkeleton variant="block" />
        </template>
        <template v-else>
            <CompassObject object="comment" size="small" />
            <el-form
                ref="annotationForm"
                :model="annotation"
                labelPosition="top"
                :inline="true"
                :inlineMessage="true"
            >
                <div
                    v-if="showHint"
                    class="annotation-form__ai-hint typographies-paragraph-small-regular"
                    v-html="$t.__('richtext.ai.hint')"
                />
                <RichText
                    v-model="annotation.description"
                    :placeholder="$t.__('forms.AnnotationForm.write_comment_or_mention_someone')"
                    :options="optionsRichText"
                    typeRichText="comment"
                    :useStorage="true"
                    :refRichText="refRichText"
                    :showAIComponents="showAIComponents"
                    class="annotation-richtext"
                    :hash="hash"
                    @focus="isEditorFocused = true"
                    @blur="isEditorFocused = false"
                >
                    <template v-if="timetrackingEnabled" #more-custom-options>
                        <TimetrackingRichText
                            v-model="annotation.timetracking"
                            :showMenuTimetracking="showMenuTimetracking"
                            :mode="mode"
                            :defaultOptions="defaultOptions"
                            :refRichText="refRichText"
                        />
                    </template>
                    <template v-if="canSaveAnnotation && !isSavingComment" #actions>
                        <CompassButton
                            :label="$t.__('common.actions.cancel')"
                            type="secondary"
                            size="small"
                            @click.prevent="resetAnnot"
                        />
                        <CompassButton
                            :label="$t.__('common.actions.save')"
                            size="small"
                            @click.prevent="submitForm('annotationForm')"
                        />
                    </template>
                </RichText>
            </el-form>
        </template>
    </div>
</template>

<script>
import FormMixins from 'mixins/FormMixins';
import RichText from 'vcomponents/richtext/RichText.vue';
import TimetrackingRichText from 'vcomponents/richtext/customoptions/TimetrackingRichText.vue';
import { REF_RICH_TEXT_CONTEXT, buildRichTextRef } from 'Services/RichTextRefBuilder.js';
import generateHash from 'Helpers/hashGenerator.js';
import Feature from 'Services/Feature';
import AnnotationService from 'Services/Annotations/AnnotationService';
import { isMarginEnabled } from 'Helpers/richTextHelper.js';

const annotationTpl = {
    description: '',
    parent_id: 0,
    timetracking: {
        duration: '',
        service: [],
    },
};
export default {
    components: {
        RichText,
        TimetrackingRichText,
    },
    mixins: [FormMixins],
    props: {
        linkedid: {
            type: [Number, Boolean],
            required: false,
            default: 0,
        },
        linkedtype: {
            type: [String],
            required: false,
            default: '',
        },
        data: {
            type: Object,
            required: false,
            default: () => {},
        },
        corptype: {
            type: String,
            required: false,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            annotation: {
                ..._.cloneDeep(annotationTpl),
                ...{ related: [{ id: this.linkedid, type: this.linkedtype }] },
            },
            mode: 'edit',
            isSavingComment: false,
            timetrackingEnabled: this.data.timetracking,
            timetrackingSubmit: false,
            defaultOptions: this.data.timetrackingOptions?.defaultServices
                ? this.data.timetrackingOptions.defaultServices
                : [],
            annotationService: new AnnotationService(),
            optionsRichText: [
                'SellsyAI',
                'Heading',
                'Bold',
                'Italic',
                'Underline',
                'Color',
                'Highlight',
                'BulletList',
                'OrderedList',
                'Divider',
                'Link',
                'Mention',
            ],
            options: [],
            showMenuTimetracking: false,
            timetrackingOpen: false,
            refRichText: '',
            hash: generateHash(),
            isEditorFocused: false,
        };
    },
    computed: {
        canSaveAnnotation() {
            return (
                this.annotation.description.length > 0 &&
                this.annotation.description.replace(/\s/gm, '') !== '<p></p>'
            );
        },
        showAIComponents() {
            return Feature.isEnabled('sellsy-ai');
        },
        showHint() {
            return this.isEditorFocused && isMarginEnabled();
        },
    },
    watch: {
        isLoading(newValue) {
            if (newValue === false) {
                this.init();
            }
        },
    },
    mounted() {
        this.init();
    },
    created() {
        this.refRichText = buildRichTextRef({
            context: REF_RICH_TEXT_CONTEXT.ANNOT_NEW,
            linkedid: this.linkedid,
            linkedtype: this.linkedtype,
        });
    },
    methods: {
        init() {
            this.timetrackingEnabled = this.data.timetracking;
            this.defaultOptions = this.data.timetrackingOptions?.defaultServices
                ? this.data.timetrackingOptions.defaultServices
                : [];
            const defaultOptions = this.defaultOptions.filter((option) => option.default);
            if (defaultOptions.length > 0) {
                this.annotation.timetracking.service = parseInt(defaultOptions[0].id);
            }
        },
        // Used with submitForm() method from FormMixins
        save(payload) {
            this.prepost();

            this.isSavingComment = true;
            let timetrackingTemp = null;

            if (this.annotation.timetracking.duration.length > 0) {
                timetrackingTemp = _.cloneDeep(this.annotation.timetracking);
                let a = timetrackingTemp.duration.split(':'); // split it at the colons
                timetrackingTemp.duration = +a[0] * 60 * 60 + +a[1] * 60;
            }

            this.annotationService
                .createAnnotation(
                    this.annotation.description,
                    this.linkedid,
                    this.corptype || this.linkedtype,
                    0,
                    timetrackingTemp,
                )
                .then((response) => {
                    this.resetAnnot();
                    if (sessionStorage.getItem(`overview__${this.refRichText}`)) {
                        sessionStorage.removeItem(`overview__${this.refRichText}`);
                    }
                    EventBus.$emit('timeline:add', 'annotation', response);
                    this.$emit('reloadAnnotations');
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);
                    Push.error(this.$l10n.__("Problème lors de l'enregistrement du commentaire"));
                })
                .finally(() => (this.isSavingComment = false));
        },
        prepost() {
            if (this.annotation.timetracking.service === 0) {
                this.annotation.timetracking.service = [];
            }
        },
        resetAnnot() {
            this.$trigger('annotationTimetracking:reset');
            this.timetrackingOpen = false;
            this.annotation = {
                ...annotationTpl,
                ...{ related: [{ id: this.linkedid, type: this.linkedtype }] },
            };

            const defaultOptions = this.defaultOptions.filter((option) => option.default);
            if (defaultOptions.length > 0) {
                this.annotation.timetracking.service = parseInt(defaultOptions[0].id);
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.annotation-form {
    align-items: flex-start;
    display: flex;
    gap: var(--spacing-1);
    position: relative;

    &__ai-hint {
        color: var(--colors-common-text-subtle-default);
        padding: var(--spacing-0) 0 0;
        position: absolute;
        right: 0;
        top: 100%;
    }

    &::v-deep {
        .el-form {
            flex: 1;
            margin: 0;
        }

        .compass-object {
            flex-shrink: 0;
        }

        .compass-skeleton__square {
            height: 32px;
            width: 32px;
        }

        .compass-skeleton:has(.compass-skeleton__square) {
            flex-shrink: 0;
        }

        .compass-skeleton__block {
            height: 101px;
        }

        .compass-skeleton:has(.compass-skeleton__block) {
            flex: 1;
        }
    }
}

.annotation-richtext {
    position: relative;
}
</style>
